import { EventEmitter } from '../../core/Event';
import type { Random } from '../../core/Random';
import type { PlinkoEvent } from './PlinkoEvents';
import type { PlinkoConfig, PlinkoStep } from './PlinkoTypes';

const DEFAULT_ROWS = 8;
const DEFAULT_PEG_SPACING_PX = 38;

export interface PlinkoDropResult {
  path: readonly PlinkoStep[];
  slot: number;
  multiplier: number;
  winnings: number;
}

/**
 * Authoritative state for a Plinko session: balance, the slot multiplier
 * roster, and the per-drop simulation. Calls to {@link drop} debit the
 * bet, run a {@code rows}-step random walk to determine the final slot,
 * credit any winnings, and emit {@code ball-dropped} with the path so
 * the host widget can animate the descent.
 */
export class PlinkoModel {
  public readonly events = new EventEmitter<PlinkoEvent>();
  public readonly bet: number;
  public readonly rows: number;
  public readonly slotMultipliers: readonly number[];
  public readonly pegSpacingPx: number;
  private readonly random: Random;
  private _balance: number;

  constructor(config: PlinkoConfig, random: Random) {
    this.rows = config.rows ?? DEFAULT_ROWS;
    if (config.slotMultipliers.length !== this.rows + 1) {
      throw new Error(
        `slotMultipliers length (${config.slotMultipliers.length}) must equal rows + 1 (${this.rows + 1})`,
      );
    }
    this.bet = config.bet;
    this.slotMultipliers = config.slotMultipliers;
    this.pegSpacingPx = config.pegSpacingPx ?? DEFAULT_PEG_SPACING_PX;
    this.random = random;
    this._balance = config.startingBalance;
  }

  get balance(): number {
    return this._balance;
  }

  /** Try to drop a ball. Returns null + emits {@code not-enough-coins}
   * when the player can't afford the bet. On success, returns the path
   * the ball took + the winning slot. The model has already credited any
   * winnings before this returns; the host animation is purely cosmetic. */
  drop(): PlinkoDropResult | null {
    if (this._balance < this.bet) {
      this.events.emit({ kind: 'not-enough-coins', required: this.bet, have: this._balance });
      return null;
    }
    const balBefore = this._balance;
    this._balance -= this.bet;
    this.events.emit({
      kind: 'balance-changed',
      before: balBefore,
      after: this._balance,
      reason: 'drop-bet',
    });

    const path: PlinkoStep[] = [];
    let slot = 0;
    for (let i = 0; i < this.rows; i++) {
      const goRight = this.random.nextBoolean();
      path.push(goRight ? 'R' : 'L');
      if (goRight) slot += 1;
    }

    const multiplier = this.slotMultipliers[slot] ?? 0;
    const winnings = Math.floor(this.bet * multiplier);
    if (winnings > 0) {
      const winBefore = this._balance;
      this._balance += winnings;
      this.events.emit({
        kind: 'balance-changed',
        before: winBefore,
        after: this._balance,
        reason: 'drop-win',
      });
    }
    this.events.emit({ kind: 'ball-dropped', path, slot, multiplier, winnings });
    return { path, slot, multiplier, winnings };
  }

  /** Convenience for the host to emit the post-animation landed event. */
  emitBallLanded(slot: number, multiplier: number, winnings: number): void {
    this.events.emit({ kind: 'ball-landed', slot, multiplier, winnings });
  }

  dispose(): void {
    this.events.clear();
  }
}
